import * as React from 'react';
import { Text } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { openConfirmModal } from '@mantine/modals';
import { useQuery } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import { useMutation } from '@tanstack/react-query';

// API
import { PatchContact, PostOrganizationContact, GetManifestContactById, queryClient } from '../../api';

// Components
import { FormScaffolding, FormHandle } from '../Form/FormScaffolding';
import { hydrateManifestSection } from '../Form/helpers/hydrateManifestSection';
import { Spinner } from '../Spinner';

// Types
import { IManifestFieldHydrated, IFormSection } from '../../manifests/types';
import { IContactRecord, IManifest } from '../../types';

const isPrimaryContact = ({
    contactId,
    ancillaryData
}: {
    contactId?: string;
    ancillaryData?: Record<string, any>;
}): boolean => {
    return contactId && ancillaryData?.organization ? contactId === ancillaryData?.organization?.primaryContact : false;
};

interface ContactFormProps {
    contact?: IManifest;
    ancillaryData?: Record<string, any>;
    contactId?: string;
    isNew?: boolean;
    isSubForm?: boolean;
    noCardStyle?: boolean;
    dirtySaveOnly?: boolean;
    hideNotification?: boolean;
    formInsert?: boolean;
    hideCancel?: boolean;
    drawerCard?: boolean;
    confirmContact?: boolean;
    saveLabelKey?: string;
    trigger?: string;
    surveyId?: string;
    onCancel?: () => void;
    onSave?: () => void;
    parentNext?: () => Promise<void>;
    parentPrev?: () => void;
    manifest?: IManifestFieldHydrated[];
    clientId?: string;
    data?: string; // contactId can come through as data via controlledDataSet from questionManifest
    manifestSections?: IFormSection<IManifestFieldHydrated>[];
}

export const defaultContactFormSections = [
    {
        manifest: hydrateManifestSection([
            { id: 'CON0001' },
            { id: 'CON0002' },
            { id: 'CON0003' },
            { id: 'CON0022' },
            { id: 'CON0023' },
            { id: 'CON0024' },
            {},
            { id: 'CON0019' },
            { id: 'CON0020' },
            {},
            { id: 'CON0005' },
            { id: 'CON0006' },
            {},
            { id: 'CON0007' },
            { id: 'CON0008' },
            { id: 'CON0009' },
            {},
            { id: 'CON0013' },
            { id: 'CON0011' },
            { id: 'CON0017' },
            { id: 'CON0018' },
            { id: 'CON0016' }
        ])
    }
];

export type ContactFormHandle = {
    onCancel: () => void;
};

export const ContactForm = React.forwardRef<ContactFormHandle, ContactFormProps>(
    ({ manifestSections = defaultContactFormSections, contactId, contact, ancillaryData, data, ...props }, ref) => {
        const {
            isInitialLoading,
            isLoading,
            data: contactData,
            isFetching
        } = useQuery({
            queryKey: ['contactById', contactId ?? data, JSON.stringify(manifestSections)],
            queryFn: async () => {
                const response = await GetManifestContactById({
                    contactId: contactId ?? data ?? '',
                    manifest: manifestSections[0]?.manifest?.map(({ id }) => id).filter(Boolean)
                });

                return response;
            },
            enabled: !contact,
            refetchOnWindowFocus: false
        });

        return (
            <ContactFormInner
                ref={ref}
                loading={!contact && (isInitialLoading || isLoading || isFetching || !contactData)}
                contact={contactData?.keyed || contact}
                contactId={contactId}
                ancillaryData={contactData?.ancillary || ancillaryData}
                manifestSections={manifestSections}
                {...props}
            />
        );
    }
);

interface ContactFormInnerProps extends ContactFormProps {
    contact?: IManifest;
    loading?: boolean;
}

export const ContactFormInner = React.forwardRef<ContactFormHandle, ContactFormInnerProps>(
    (
        {
            contact = {},
            contactId,
            isNew,
            isSubForm,
            noCardStyle,
            saveLabelKey,
            dirtySaveOnly,
            hideNotification,
            onCancel,
            onSave,
            parentNext,
            surveyId,
            drawerCard = true,
            confirmContact,
            formInsert,
            ancillaryData = {},
            hideCancel,
            parentPrev,
            clientId,
            loading,
            manifest = defaultContactFormSections[0].manifest,
            manifestSections,
            trigger
        },
        ref
    ) => {
        const FormRef = React.useRef<FormHandle>(null);
        const { t } = useTranslation();
        const openDiscardChangesModal = React.useCallback(({ confirmCallback }: { confirmCallback: () => void }) => {
            openConfirmModal({
                title: t('modals.confirmDiscardChanges.title', { ns: 'components' }),
                children: <Text size="sm">{t('modals.confirmDiscardChanges.body', { ns: 'components' })}</Text>,
                labels: {
                    confirm: t('modals.confirmDiscardChanges.confirmCTA', { ns: 'components' }),
                    cancel: t('cancel', { ns: 'common' })
                },
                confirmProps: { color: 'red' },
                onCancel: () => console.log('Cancel'),
                onConfirm: () => {
                    confirmCallback();
                }
            });
        }, []);

        const patchContact = useMutation(PatchContact, {
            onSuccess: async (data, variables) => {
                if (surveyId) {
                    await queryClient.invalidateQueries({
                        queryKey: ['survey', surveyId],
                        exact: true,
                        refetchType: 'active'
                    });
                }
                if (trigger === 'onboarding') {
                    await queryClient.invalidateQueries({
                        queryKey: ['onboardingSurvey', clientId],
                        exact: true,
                        refetchType: 'active'
                    });
                }
                !hideNotification &&
                    notifications.show({
                        title: t('updatedContact.title', {
                            ns: 'notifications'
                        }),
                        message: t('updatedContact.message', {
                            ns: 'notifications'
                        })
                    });
                onSave?.();
                queryClient.invalidateQueries({
                    queryKey: ['contactById', contactId],
                    exact: true,
                    refetchType: 'active'
                });
            }
        });
        const postNewContact = useMutation(PostOrganizationContact, {
            onSuccess: async (data, variables) => {
                queryClient.invalidateQueries({
                    queryKey: ['contactById', contactId],
                    exact: true,
                    refetchType: 'active'
                });
                if (variables?.clientId) {
                    queryClient.invalidateQueries({
                        queryKey: ['contactsById', variables?.clientId],
                        exact: true,
                        refetchType: 'active'
                    });
                }
                if (surveyId) {
                    await queryClient.invalidateQueries({
                        queryKey: ['survey', surveyId],
                        exact: true,
                        refetchType: 'active'
                    });
                }
                if (trigger === 'onboarding') {
                    await queryClient.invalidateQueries({
                        queryKey: ['onboardingSurvey', clientId],
                        exact: true,
                        refetchType: 'active'
                    });
                }
                !hideNotification &&
                    notifications.show({
                        title: t('createdContact.title', {
                            ns: 'notifications'
                        }),
                        message: t('createdContact.message', {
                            ns: 'notifications'
                        })
                    });
                onSave?.();
            }
        });
        const onClickSave = React.useCallback(
            async (values: Record<string, any>) => {
                if (contactId) {
                    await patchContact.mutate({
                        contactId: contactId,
                        contact: values,
                        surveyId
                    });
                } else if (clientId) {
                    await postNewContact.mutate({
                        clientId,
                        contact: values,
                        surveyId
                    });
                }
                // onSave?.();
                // return Promise.resolve();
            },
            [surveyId, patchContact, contactId, clientId]
        );
        const onCancelEdit = React.useCallback(() => {
            if (FormRef?.current?.isDirty()) {
                openDiscardChangesModal({
                    confirmCallback: () => {
                        onCancel?.();
                        FormRef?.current?.reset();
                    }
                });
            } else {
                onCancel?.();
            }
        }, [FormRef]);

        React.useImperativeHandle(
            ref,
            () => ({
                onCancel: () => {
                    return onCancelEdit();
                }
            }),
            [onCancelEdit]
        );

        return (
            <>
                {!isNew && (loading || !contact) ? (
                    <Spinner />
                ) : (
                    <FormScaffolding
                        ref={FormRef}
                        sections={manifestSections ?? [{ manifest }]}
                        drawerCard={drawerCard}
                        isSubForm={isSubForm}
                        noCardStyle={noCardStyle}
                        saveLabelKey={saveLabelKey}
                        confirmContact={confirmContact}
                        dirtySaveOnly={dirtySaveOnly}
                        formInsert={formInsert}
                        ancillaryData={
                            ancillaryData
                                ? {
                                      ...ancillaryData,
                                      isPrimaryContact: isPrimaryContact({ ancillaryData, contactId })
                                  }
                                : {}
                        }
                        disableValidation={false}
                        key={contact?.id}
                        data={contact}
                        onSave={async ({ values }) => {
                            onClickSave?.(values);
                        }}
                        onCancel={onCancelEdit}
                        hideCancel={hideCancel}
                        parentPrev={parentPrev}
                        externalIsLoading={patchContact?.isLoading || postNewContact?.isLoading}
                    />
                )}
            </>
        );
    }
);

export default ContactForm;
